import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, FileDown, Save, Send } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, downloadFile } from "@/lib/api";
import { addDays, cleanNum, fmtDate, fmtStamp } from "@/lib/format";
import { cn } from "@/lib/utils";
import { PageHeader, StatusBadge, LoadingBlock, Spinner } from "@/components/Shared";
import { TimesheetTotals, dayLabel } from "@/components/TimesheetTable";

const toNum = (x) => (x === "" || x == null ? null : Number(x));
const ints = (x) => String(x ?? "").replace(/[^0-9]/g, "").slice(0, 2);

function calcTotals(rows) {
  const mins = rows.reduce((a, r) => a + (Number(r.hours) || 0) * 60 + (Number(r.minutes) || 0), 0);
  return { miles: rows.reduce((a, r) => a + (Number(r.miles) || 0), 0), atv_days: rows.filter((r) => r.atv).length, hours: Math.round((mins / 60) * 100) / 100 };
}

function DayRow({ row, onChange, locked }) {
  const d = dayLabel(row.date);
  const set = (k, val) => onChange({ ...row, [k]: val });
  const num = (k, label, clean, mode) => (
    <label className="block">
      <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</span>
      <input className="field-input mt-1 h-11 text-right font-mono-num" disabled={locked} inputMode={mode} value={row[k] ?? ""} onChange={(e) => set(k, clean(e.target.value))} data-testid={`ts-${k}-${row.date}`} />
    </label>
  );
  return (
    <div className="panel p-3" data-testid={`ts-day-${row.date}`}>
      <div className="mb-2 flex items-center justify-between">
        <p className="font-semibold text-slate-900">{d.dow} <span className="font-normal text-slate-500">{d.md}</span></p>
        <button type="button" disabled={locked} onClick={() => set("atv", !row.atv)} data-testid={`ts-atv-${row.date}`} aria-pressed={row.atv}
          className={cn("h-9 rounded-full border px-4 text-xs font-bold uppercase tracking-wider transition-colors", row.atv ? "border-[#5C241E] bg-[#5C241E] text-white" : "border-border text-slate-600")}>
          ATV {row.atv ? "Yes" : "No"}
        </button>
      </div>
      <input className="field-input h-11" disabled={locked} aria-label={`Location ${d.dow} ${d.md}`} placeholder="Location" value={row.location} maxLength={120} onChange={(e) => set("location", e.target.value)} data-testid={`ts-location-${row.date}`} />
      <div className="mt-2 grid grid-cols-3 gap-2">
        {num("miles", "Miles", cleanNum, "decimal")}
        {num("hours", "Hours", ints, "numeric")}
        {num("minutes", "Minutes", ints, "numeric")}
      </div>
    </div>
  );
}

export default function Timesheet() {
  const [date, setDate] = useState(null);
  const [ts, setTs] = useState(null);
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    setTs(null);
    api.get("/timesheets/mine", { params: date ? { date } : {} }).then(({ data }) => { setTs(data); setRows(data.rows); }).catch((e) => toast.error(errMsg(e)));
  }, [date]);

  if (!ts) return <LoadingBlock />;
  const locked = ts.status === "submitted";
  const update = (i, r) => setRows(rows.map((x, j) => (j === i ? r : x)));
  const save = async (submit) => {
    for (const r of rows) {
      if (Number(r.hours) > 24 || Number(r.minutes) > 59) return toast.error(`Check hours/minutes on ${fmtDate(r.date)}`);
    }
    setBusy(submit ? "submit" : "save");
    try {
      const payload = rows.map((r) => ({ ...r, miles: toNum(r.miles), hours: toNum(r.hours), minutes: toNum(r.minutes) }));
      const { data } = await api.put("/timesheets/mine", { period_start: ts.period_start, rows: payload, submit });
      setTs(data); setRows(data.rows);
      toast.success(submit ? "Timesheet submitted" : "Timesheet saved");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };

  return (
    <>
    <div className="fade-up pb-40 md:pb-24">
      <PageHeader eyebrow="Pay period" title="Timesheet" />
      <div className="panel mb-4 flex items-center justify-between p-2">
        <button className="btn-ghost h-11 w-11 px-0" onClick={() => setDate(addDays(ts.period_start, -14))} data-testid="ts-prev-period" aria-label="Previous period"><ChevronLeft className="h-5 w-5" /></button>
        <div className="text-center">
          <p className="font-semibold" data-testid="ts-period-label">{fmtDate(ts.period_start)} – {fmtDate(ts.period_end)}</p>
          <div className="mt-1"><StatusBadge status={ts.status} testId="ts-status" /></div>
        </div>
        <button className="btn-ghost h-11 w-11 px-0" onClick={() => setDate(addDays(ts.period_start, 14))} data-testid="ts-next-period" aria-label="Next period"><ChevronRight className="h-5 w-5" /></button>
      </div>
      {ts.status === "new" && <p className="mb-3 text-sm text-slate-600" data-testid="ts-prefill-note">{"Fill in each day you worked: location, miles, ATV and hours, then submit."}</p>}
      {locked && <p className="mb-3 rounded-md border border-emerald-300 bg-emerald-50 px-3 py-2 text-sm text-emerald-900" data-testid="ts-locked-note">Submitted and locked. If something needs to change, ask an admin to unlock it.</p>}
      <TimesheetTotals totals={calcTotals(rows)} />
      {[0, 7].map((w) => (
        <section key={w} className="mt-5">
          <h2 className="mb-2 font-display text-lg font-semibold">Week {w / 7 + 1}</h2>
          <div className="space-y-2">{rows.slice(w, w + 7).map((r, i) => <DayRow key={r.date} row={r} locked={locked} onChange={(nr) => update(w + i, nr)} />)}</div>
        </section>
      ))}
    </div>
      <div className="pb-safe fixed inset-x-0 bottom-16 z-30 border-t border-border bg-white/95 p-3 backdrop-blur md:bottom-0">
        <div className="mx-auto flex max-w-3xl gap-2">
          {ts.id && <button className="btn-ghost h-12 px-3" onClick={() => downloadFile(`/timesheets/${ts.id}/export`, { format: "pdf" })} data-testid="ts-download-pdf" aria-label="Download PDF"><FileDown className="h-5 w-5" /></button>}
          {!locked && <button className="btn-ghost h-12 flex-1" disabled={!!busy} onClick={() => save(false)} data-testid="ts-save-button">{busy === "save" ? <Spinner /> : <Save className="h-4 w-4" />}Save</button>}
          {!locked && <button className="btn-brand h-12 flex-1" disabled={!!busy} onClick={() => save(true)} data-testid="ts-submit-button">{busy === "submit" ? <Spinner className="text-white" /> : <Send className="h-4 w-4" />}Submit</button>}
          {locked && <p className="flex h-12 flex-1 items-center text-sm font-semibold text-emerald-800" data-testid="ts-locked-bar">Submitted {ts.submitted_at ? fmtStamp(ts.submitted_at) : ""}</p>}
        </div>
      </div>
    </>
  );
}
